import { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import usePlayers from "./hooks/usePlayers";
import { PlayersService } from "../../../../core/http/services/PlayersService";

const emptyPlayerForm = {
  name: "",
  email: "",
  phone: "",
  birthDate: "",
  password: "",
};

const Players = () => {
  const { players, setPlayers, pagination, fetchPlayers } = usePlayers();

  const [search, setSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPlayers({ search, page: 1 });
    }, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [search, fetchPlayers]);

  const handlePageChange = (_, newPage) => {
    fetchPlayers({ page: newPage, search });
  };

  const [newPlayerOpen, setNewPlayerOpen] = useState(false);
  const [newPlayerForm, setNewPlayerForm] = useState(emptyPlayerForm);

  const [editPlayerOpen, setEditPlayerOpen] = useState(false);
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [editPlayerForm, setEditPlayerForm] = useState(emptyPlayerForm);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState(null);
  const [temporaryPassword, setTemporaryPassword] = useState(null);
  const [passwordCopied, setPasswordCopied] = useState(false);

  const handleOpenEdit = (player) => {
    setEditingPlayerId(player.id);
    setEditPlayerForm({
      name: player.name,
      email: player.email ?? "",
      phone: player.phone ?? "",
      birthDate: player.birthDate ? player.birthDate.slice(0, 10) : "",
    });
    setEditError(null);
    setResetPasswordError(null);
    setTemporaryPassword(null);
    setPasswordCopied(false);
    setEditPlayerOpen(true);
  };

  const handleCloseEdit = () => {
    setEditPlayerOpen(false);
    setEditingPlayerId(null);
    setEditError(null);
    setResetPasswordError(null);
    setTemporaryPassword(null);
    setPasswordCopied(false);
  };

  const handleResetPassword = async () => {
    if (!editingPlayerId) return;
    setResetPasswordLoading(true);
    setResetPasswordError(null);
    setTemporaryPassword(null);
    setPasswordCopied(false);

    const { status, data, message } = await PlayersService.resetPassword(editingPlayerId);
    setResetPasswordLoading(false);

    if (status !== 200 || !data) {
      setResetPasswordError(message || "Erro ao gerar nova senha.");
      return;
    }

    setTemporaryPassword(data.temporaryPassword);
  };

  const handleCopyPassword = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(temporaryPassword);
    } else {
      const el = document.createElement("input");
      el.type = "text";
      el.value = temporaryPassword;
      el.style.position = "fixed";
      el.style.opacity = "0";
      el.style.pointerEvents = "none";
      const dialog = document.querySelector('[role="dialog"]');
      const container = dialog ?? document.body;
      container.appendChild(el);
      el.focus();
      el.select();
      document.execCommand("copy");
      container.removeChild(el);
    }
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  const handleSaveNewPlayer = () => {
    const newPlayer = {
      id: `p-${Date.now()}`,
      name: newPlayerForm.name || "Novo Jogador",
      email: newPlayerForm.email || "",
      phone: newPlayerForm.phone || "",
      birthDate: newPlayerForm.birthDate || null,
    };

    setPlayers((prev) => [newPlayer, ...prev]);
    setNewPlayerForm(emptyPlayerForm);
    setNewPlayerOpen(false);
  };

  const handleSaveEditPlayer = async () => {
    if (!editingPlayerId) return;
    setEditLoading(true);
    setEditError(null);

    const { status, data, message } = await PlayersService.patch(editingPlayerId, {
      name: editPlayerForm.name,
      email: editPlayerForm.email,
      phone: editPlayerForm.phone,
      birthDate: editPlayerForm.birthDate || null,
    });

    setEditLoading(false);

    if (status !== 200 || !data) {
      setEditError(message || "Erro ao salvar as alterações.");
      return;
    }

    setPlayers((prev) =>
      prev.map((player) =>
        player.id === editingPlayerId
          ? {
              ...player,
              name: data.name,
              email: data.email,
              phone: data.phone ?? player.phone,
              birthDate: data.birthDate ?? player.birthDate,
            }
          : player,
      ),
    );

    handleCloseEdit();
  };

  const handleFieldChange = (setter, field) => (event) => {
    setter((prev) => ({ ...prev, [field]: event.target.value }));
  };

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
            Jogadores
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            Gerencie cadastro, status e credenciais dos jogadores.
          </Typography>
        </Box>

        {/* <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNewPlayerOpen(true)}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Adicionar jogador
        </Button> */}
      </Stack>

      <TextField
        placeholder="Buscar por nome..."
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ maxWidth: 320 }}
      />

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 1.2,
        }}
      >
        <Stack spacing={1}>
          {players.map((player) => (
            <Paper
              key={player.id}
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                p: 1.1,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={1}
              >
                <Stack
                  direction="row"
                  spacing={1.2}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <Avatar
                    src={player.avatarUrl || undefined}
                    sx={{ bgcolor: "primary.main" }}
                  >
                    {player.name[0]}
                  </Avatar>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 800 }}>
                      {player.name}
                    </Typography>
                    <Typography noWrap variant="body2" color="text.secondary">
                      {player.email}
                    </Typography>
                  </Box>
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenEdit(player)}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Editar
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Paper>

      {pagination.totalPages > 1 && (
        <Pagination
          count={pagination.totalPages}
          page={pagination.page}
          onChange={handlePageChange}
          sx={{ alignSelf: "center" }}
        />
      )}

      <Dialog
        open={newPlayerOpen}
        onClose={() => setNewPlayerOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Adicionar novo jogador</DialogTitle>
        <DialogContent>
          <Stack spacing={1.3} sx={{ mt: 0.5 }}>
            <TextField
              label="Nome"
              fullWidth
              value={newPlayerForm.name}
              onChange={handleFieldChange(setNewPlayerForm, "name")}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={newPlayerForm.email}
              onChange={handleFieldChange(setNewPlayerForm, "email")}
            />
            <TextField
              label="Nascimento"
              type="date"
              fullWidth
              value={newPlayerForm.birthDate}
              onChange={handleFieldChange(setNewPlayerForm, "birthDate")}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Senha"
              type="password"
              fullWidth
              value={newPlayerForm.password}
              onChange={handleFieldChange(setNewPlayerForm, "password")}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewPlayerOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveNewPlayer}>
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editPlayerOpen}
        onClose={handleCloseEdit}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar jogador</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome"
              fullWidth
              value={editPlayerForm.name}
              onChange={handleFieldChange(setEditPlayerForm, "name")}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={editPlayerForm.email}
              onChange={handleFieldChange(setEditPlayerForm, "email")}
            />
            <TextField
              label="Telefone"
              fullWidth
              value={editPlayerForm.phone}
              onChange={handleFieldChange(setEditPlayerForm, "phone")}
            />
            <TextField
              label="Nascimento"
              type="date"
              fullWidth
              value={editPlayerForm.birthDate}
              onChange={handleFieldChange(setEditPlayerForm, "birthDate")}
              InputLabelProps={{ shrink: true }}
            />

            {temporaryPassword && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "success.main",
                  bgcolor: "rgba(46, 125, 50, 0.06)",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: "success.dark", display: "block", mb: 1 }}
                >
                  Nova senha temporária gerada com sucesso
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: 900,
                      fontSize: "1.25rem",
                      letterSpacing: 3,
                      color: "success.dark",
                      flex: 1,
                    }}
                  >
                    {temporaryPassword}
                  </Typography>
                  <Tooltip title={passwordCopied ? "Copiado!" : "Copiar senha"}>
                    <IconButton
                      size="small"
                      onClick={handleCopyPassword}
                      color={passwordCopied ? "success" : "default"}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Compartilhe esta senha com o jogador para o próximo acesso.
                </Typography>
              </Box>
            )}

            {resetPasswordError && (
              <Typography variant="caption" color="error">
                {resetPasswordError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color="warning"
            onClick={handleResetPassword}
            disabled={resetPasswordLoading || editLoading}
            startIcon={
              resetPasswordLoading
                ? <CircularProgress size={16} color="inherit" />
                : <LockResetIcon />
            }
            sx={{ mr: "auto", textTransform: "none", fontWeight: 700 }}
          >
            {resetPasswordLoading ? "Gerando..." : "Gerar nova senha"}
          </Button>
          {editError && (
            <Typography variant="caption" color="error" sx={{ mx: 1 }}>
              {editError}
            </Typography>
          )}
          <Button onClick={handleCloseEdit} disabled={editLoading || resetPasswordLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEditPlayer}
            disabled={editLoading}
            startIcon={editLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {editLoading ? "Salvando..." : "Salvar alterações"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default Players;
